import { useForm } from 'react-hook-form';
import { FormProvider, Table, TextField, Checkbox, SelectTag } from '../../../../../../components';
import { Button, Container, Dialog, Grid, IconButton, Paper, Stack, Typography, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dropdownFetch, getKeyPathValue, request, toast } from '../../../../../../utils';
import { CALCULATION_TYPE, REG_LOCATION } from '../../../../../../constants';
import { RoomMasterBedForm } from './RoomMasterBedForm';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../../components';

const TABLE_COLUMNS = [
  {
    header: 'Ward Name',
    accessor: (row) => {
      const status = row.status;
      return (
        <span
          style={{
            color: status === 'inactive' ? 'red' : 'black'
          }}
        >
          {`${row.ward.name}`}
        </span>
      );
    }
  },
  {
    header: 'Room No.',
    accessor: 'room_no'
  },
  {
    header: 'Department unit',
    accessor: 'department_unit.name'
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: { textTransform: 'uppercase' }
  },
  {
    header: 'Created At',
    accessor: 'createdAt'
  }
];

const BED_TABLE_HEADER = [
  {
    header: 'Bed No.',
    accessor: 'bed_no'
  },
  {
    header: 'Occupy Status',
    accessor: 'occupy_status'
  },
  {
    header: 'Cradle Charge',
    accessor: 'cradle_charge'
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: { textTransform: 'uppercase' }
  }
];

const initialRoomDataObj = {
  data: [],
  loading: false
};

export const RoomMaster = () => {
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [roomDataObj, setRoomDataObj] = useState(initialRoomDataObj);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [addDetailsDialogOpen, setAddDetailsDialogOpen] = useState(false);
  const [bedDetailList, setBedDetailList] = useState([]);
  const [nurseStationDropdown, setNurseStationDropdown] = useState([]);
  const [departmentDropdown, setDepartmentDropdown] = useState([]);
  const [departmentUnitDropdown, setDepartmentUnitDropdown] = useState([]);
  const [doctorDropdown, setDoctorDropdown] = useState([]);
  const [floorDropdown, setFloorDropdown] = useState([]);
  const [wardDropdown, setWardDropdown] = useState([]);
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        nurse_station: Yup.string().required(' '),
        ward: Yup.string().required(' '),
        location: Yup.string().required(' '),
        room_no: Yup.string().required(' '),
        floor: Yup.string().required(' '),
        charge: Yup.string().required(' ')
      })
    ),
    defaultValues: { location: 'Hospital' },
    mode: 'all'
  });

  const { getValues } = useForm();
  const { handleSubmit, setValue, reset, watch } = methods;

  const getNurseStationDropdown = useCallback(async () => {
    const data = await dropdownFetch('nursing-station');
    if (!selectedRecord) {
      const [defaultSponsor] = data.filter(({ status }) => status === 'default');
      setValue('nurse_station', defaultSponsor?._id);
    }
    setNurseStationDropdown(data);
  }, []);

  const getDepartmentDropdown = useCallback(async () => {
    const data = await dropdownFetch('department');
    setDepartmentDropdown(data);
  }, []);

  const getDepartmentUnitDropdown = useCallback(async () => {
    const data = await dropdownFetch('department-unit');
    if (!selectedRecord) {
      const [defaultReferal] = data.filter(({ status }) => status === 'default');
      setValue('department_unit', defaultReferal?._id);
    }
    setDepartmentUnitDropdown(data);
  }, []);

  const getDoctorDropdown = useCallback(async () => {
    const data = await dropdownFetch('department-unit-head');
    if (!selectedRecord && !getValues('department-unit-head')) {
      const [defaultState] = data.filter(({ status }) => status === 'default');
      setValue('departmentunithead', defaultState?._id);
    }
    setDoctorDropdown(data);
  }, [selectedRecord, getValues, setValue]);

  const getFloorDropdown = useCallback(async () => {
    const data = await dropdownFetch('floor');
    if (!selectedRecord) {
      const [defaultSponsor] = data.filter(({ status }) => status === 'default');
      setValue('floor', defaultSponsor?._id);
    }
    setFloorDropdown(data);
  }, []);

  const getWardDropdown = useCallback(async () => {
    const data = await dropdownFetch('ward');
    setWardDropdown(data);
  }, []);

  useEffect(() => {
    getNurseStationDropdown();
    getDepartmentDropdown();
    getDepartmentUnitDropdown();
    getDoctorDropdown();
    getFloorDropdown();
    getWardDropdown();
  }, [
    getNurseStationDropdown,
    getDepartmentDropdown,
    getDepartmentUnitDropdown,
    getDoctorDropdown,
    getFloorDropdown,
    getWardDropdown
  ]);

  useEffect(() => {
    if (selectedRecord) {
      const keyValuePair = getKeyPathValue(selectedRecord, '', ['_id', '__v', 'status', 'createdAt', 'beds']);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
      setBedDetailList(selectedRecord.beds);
    } else {
      reset();
    }
  }, [setValue, reset, selectedRecord]);

  const formSubmit = async (formValues) => {
    setLoading(true);
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    const response = await request(editFlag ? '/room-master-update' : '/room-master-create', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: { ...formValues, beds: bedDetailList }
    });
    if (response?.success) {
      toast(`Successfully ${editFlag ? 'updated' : 'created'} Room master`, { variant: 'success' });
      setSelectedRecord(response.data);
      setCurrentAction();
      reset();
    } else {
      toast(`Failed to ${editFlag ? 'update' : 'create'} Room master`, {
        variant: 'error'
      });
    }
    setLoading(false);
  };

  const handleCancelRecord = async () => {
    setLoading(true);
    const response = await request('/room-master-cancel', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully Cancelled Room master', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to Cancelled Room master', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/room-master-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted Room master', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to delete Room master', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setRoomDataObj({ loading: true });
    const response = await request('/room-master-list', { method: 'GET' });
    if (response.success) {
      const filteredData = response.data.filter(
        (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
      );
      setRoomDataObj({ loading: false, data: filteredData });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setRoomDataObj({ loading: false });
    }
  };

  const handleBedRowDelete = (rowId) => {
    setBedDetailList((prev) => prev.filter((data) => data.bed_no !== rowId));
  };

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Room Master
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                    setBedDetailList([]);
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                    handleCancelRecord();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  onClick={handleQueryWindow}
                  disabled={['new', 'edit'].includes(currentAction)}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn
                  id={'Exit'}
                  variant="contained"
                  onClick={() => {
                    setCurrentAction('');
                  }}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '100%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={1}>
                <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Nurse Station</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      name="nurse_station"
                      // menus={nurseStationDropdown}
                      menus={nurseStationDropdown.map((data) => ({ ...data, name: data.name }))}
                      disabled={!['new', 'edit'].includes(currentAction)}
                      placeholder="Select Nurse Station"
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Unit Head</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      name="departmentunithead"
                      menus={doctorDropdown}
                      disabled={!['new', 'edit'].includes(currentAction)}
                      placeholder="Select Unit Head"
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Room No.</Typography>
                    <TextField
                      sx={{ flex: 0.8, border: currentAction ? '0.5px solid red' : '' }}
                      disabled={!['new', 'edit'].includes(currentAction)}
                      name="room_no"
                      placeholder="Room Number"
                    />
                  </Stack>

                  {/* <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Cradle Charge</Typography>
                  <TextField
                    sx={{ flex: 0.8 }}
                    disabled={!['new', 'edit'].includes(currentAction)}
                    name="cradle_charge"
                    type="number"
                    inputProps={{ min: '0' }}
                  />
                </Stack> */}
                </Grid>
                <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Location</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      name="location"
                      menus={REG_LOCATION}
                      disabled={!['new', 'edit'].includes(currentAction)}
                      placeholder="Select Location"
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Ward Name</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8, border: currentAction ? '0.5px solid red' : '' }}
                      disabled={!['new', 'edit'].includes(currentAction)}
                      name="ward"
                      menus={wardDropdown}
                      placeholder="Select Ward"
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Charge</Typography>
                    <TextField
                      sx={{ flex: 0.8, border: currentAction ? '0.5px solid red' : '' }}
                      disabled={!['new', 'edit'].includes(currentAction)}
                      name="charge"
                      type="number"
                      inputProps={{ min: '0' }}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Department Unit</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.7 }}
                      disabled={!['new', 'edit'].includes(currentAction)}
                      name="department_unit"
                      menus={departmentUnitDropdown.map((data) => ({ ...data, name: data.name }))}
                      placeholder="Select Department Unit"
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Floor</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.7 }}
                      name="floor"
                      disabled={!['new', 'edit'].includes(currentAction)}
                      menus={floorDropdown}
                      placeholder="Select Floor"
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Calc. Type</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.7 }}
                      disabled={!['new', 'edit'].includes(currentAction)}
                      name="calculation_type"
                      placeholder="Select Calculation Type"
                      menus={CALCULATION_TYPE}
                    />
                  </Stack>

                  {/* <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Price Category</Typography>
                  <TextField sx={{ flex: 0.8 }} name="price_category" disabled />
                </Stack> */}
                </Grid>
                <Grid item xs={12}>
                  <Stack spacing={1} direction="row" justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={() => setAddDetailsDialogOpen(true)}
                      disabled={!['new', 'edit'].includes(currentAction) || !watch('room_no')}
                    >
                      Add Details
                    </Button>
                  </Stack>
                  <Table
                    sx={{ mt: 1 }}
                    data={bedDetailList.map((data) => ({ _id: data.bed_no, ...data }))}
                    columns={BED_TABLE_HEADER}
                    size="small"
                    showDeleteAction={['new', 'edit'].includes(currentAction)}
                    onDeleteHandler={handleBedRowDelete}
                  />
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
      <Dialog
        open={addDetailsDialogOpen}
        onClose={() => setAddDetailsDialogOpen(false)}
        PaperProps={{ sx: { p: 2, width: '35rem' } }}
        maxWidth="xl"
        scroll="body"
      >
        <RoomMasterBedForm
          onClose={() => setAddDetailsDialogOpen(false)}
          onAdd={(row) => {
            if (bedDetailList.some((bed) => bed.bed_no === row.bed_no)) {
              toast('Bed number already taken', { variant: 'error' });
            } else {
              setBedDetailList((prev) => [...prev, row]);
            }
          }}
        />
      </Dialog>
      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', p: 1.5 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0.2}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Room Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={TABLE_COLUMNS}
          data={roomDataObj.data || []}
          loading={roomDataObj.loading}
          onRowClick={(row) => {
            const { nurse_station, floor, doctor, department, department_unit, ward, ...others } = row;
            setSelectedRecord({
              ...others,
              nurse_station: nurse_station?._id,
              floor: floor?._id,
              doctor: doctor?._id,
              department: department?._id,
              department_unit: department_unit?._id,
              ward: ward?._id
            });
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
    </>
  );
};
